import React, { useContext, useEffect, useState } from 'react';
import { BlockOutlined, EllipsisOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Area, User, Location } from '../../../models';
import { EntityChangeLog } from '../../reports';
import { UserContext } from '../../contexts';

export function AreasPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState<Area[]>();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    loadAreas();
  }, []);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'areas.edit'));
  }, [user]);

  const loadAreas = () => {
    setLoading(true);
    apiClient
      .get('areas')
      .then(response => {
        setAreas(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const syncWithSAP = () => {
    setLoading(true);
    apiClient
      .get('areas/integration')
      .then(() => {
        loadAreas();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadAreas();
  };

  const onSyncClick = () => {
    syncWithSAP();
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Lokasyon',
      dataIndex: 'location',
      key: 'location',
      render: (location: Location) => location?.name,
    },
    {
      title: 'Sorumlu',
      dataIndex: 'responsibleUser',
      key: 'responsibleUser',
      render: (responsibleUser: User) => responsibleUser?.fullNameId,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, area: Area) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(area.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <BlockOutlined /> Alanlar
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button type="primary" key="sync" onClick={onSyncClick} icon={<SyncOutlined />}>
                SAP ile eşle
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={areas} rowKey="id" loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Area'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
